import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';

import { Button } from 'components/core/Button';
import { Form, FormItem, TextArea, TextInput } from 'components/core/Form';
import { IconText } from 'components/core/IconText';
import { RadioInput, RadioGroup } from 'components/core/RadioInput';
import { Link } from 'components/core/Link';
import { StarInput } from 'components/form';
import { Text } from 'components/core/Text';
import { SuccessMessage } from 'components/form';

import validate from './ReviewFormValidation';
import './ReviewForm.scss';

function resolveValidation(name, allValues) {
	const errors = validate(allValues);
	return errors[name] || true;
}

export function ReviewForm({
	confirmationCode,
	requireConfirmationCode = true,
	onSubmit: onSubmitProp,
	facilityId,
}) {
	const [formError, setFormError] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);

	const { control, handleSubmit, formState: { isSubmitting, isValid }, getValues } = useForm({
		mode: 'onBlur',
		defaultValues: {
			rating: '',
			ratingPrice: '',
			ratingCleanliness: '',
			ratingService: '',
			ratingRecommended: '',
			title: '',
			message: '',
			nickname: '',
			email: '',
			confirmationCode: confirmationCode || '',
		},
	});

	const wrappedSubmit = useCallback(
		(formData) => {
			setFormError(null);
			const submitData = {
				...formData,
				facilityId,
				confirmationCode: !requireConfirmationCode
					? 'CONFIRMATION_CODE_NOT_REQUIRED'
					: formData.confirmationCode,
			};
			return onSubmitProp(submitData).then((response) => {
				if (response.error) {
					setFormError(response.error.message);
				} else {
					setSuccessMessage(response.payload.message);
				}
				return response;
			});
		},
		[onSubmitProp, facilityId, requireConfirmationCode],
	);

	if (successMessage) {
		return <SuccessMessage successMessage={successMessage} />;
	}

	return (
		<div className="review-form">
			<Form
				onSubmit={handleSubmit(wrappedSubmit)}
				invalid={!isValid}
				successLabel="success-review-form"
				failureLabel="fail-review-form"
				id="review-form"
			>
				<div className="ratings">
					<FormItem>
						<Controller
							name="rating"
							control={control}
							rules={{
								validate: (value) => resolveValidation('rating', { ...getValues(), rating: value }),
							}}
							render={({ field, fieldState }) => (
								<StarInput
									input={field}
									meta={{
										touched: fieldState.isTouched || fieldState.invalid,
										error: fieldState.error?.message,
										valid: !fieldState.invalid,
									}}
									id="rating"
									classsName="primary"
									label="Overall rating (1-5 stars)"
									halfStep
									size="large"
								/>
							)}
						/>
					</FormItem>
					<FormItem>
						<Controller
							name="ratingPrice"
							control={control}
							rules={{
								validate: (value) => resolveValidation('ratingPrice', { ...getValues(), ratingPrice: value }),
							}}
							render={({ field, fieldState }) => (
								<StarInput
									input={field}
									meta={{
										touched: fieldState.isTouched || fieldState.invalid,
										error: fieldState.error?.message,
										valid: !fieldState.invalid,
									}}
									id="ratingPrice"
									label="Pricing"
								/>
							)}
						/>
					</FormItem>
					<FormItem>
						<Controller
							name="ratingCleanliness"
							control={control}
							rules={{
								validate: (value) => resolveValidation('ratingCleanliness', { ...getValues(), ratingCleanliness: value }),
							}}
							render={({ field, fieldState }) => (
								<StarInput
									input={field}
									meta={{
										touched: fieldState.isTouched || fieldState.invalid,
										error: fieldState.error?.message,
										valid: !fieldState.invalid,
									}}
									id="ratingCleanliness"
									label="Cleanliness"
								/>
							)}
						/>
					</FormItem>
					<FormItem>
						<Controller
							name="ratingService"
							control={control}
							rules={{
								validate: (value) => resolveValidation('ratingService', { ...getValues(), ratingService: value }),
							}}
							render={({ field, fieldState }) => (
								<StarInput
									input={field}
									meta={{
										touched: fieldState.isTouched || fieldState.invalid,
										error: fieldState.error?.message,
										valid: !fieldState.invalid,
									}}
									label="Customer service"
								/>
							)}
						/>
					</FormItem>
				</div>
				<FormItem>
					<Controller
						name="ratingRecommended"
						control={control}
						rules={{
							validate: (value) => resolveValidation('ratingRecommended', { ...getValues(), ratingRecommended: value }),
						}}
						render={({ field, fieldState }) => (
							<RadioGroup
								input={field}
								meta={{
									touched: fieldState.isTouched || fieldState.invalid,
									error: fieldState.error?.message,
									valid: !fieldState.invalid,
								}}
								label="Would you recommend this facility to a friend?"
							>
								<RadioInput
									label="Yes"
									value="1"
								/>
								<RadioInput
									label="No"
									value="0"
								/>
							</RadioGroup>
						)}
					/>
				</FormItem>
				<FormItem>
					<Controller
						name="title"
						control={control}
						rules={{
							validate: (value) => resolveValidation('title', { ...getValues(), title: value }),
						}}
						render={({ field, fieldState }) => (
							<TextInput
								input={field}
								meta={{
									touched: fieldState.isTouched || fieldState.invalid,
									error: fieldState.error?.message,
									valid: !fieldState.invalid,
								}}
								label="One-line summary"
								helpText="Character limit: 75 (Minimum 5)"
							/>
						)}
					/>
				</FormItem>
				<FormItem>
					<Controller
						name="message"
						control={control}
						rules={{
							validate: (value) => resolveValidation('message', { ...getValues(), message: value }),
						}}
						render={({ field, fieldState }) => (
							<TextArea
								input={field}
								meta={{
									touched: fieldState.isTouched || fieldState.invalid,
									error: fieldState.error?.message,
									valid: !fieldState.invalid,
								}}
								label="Review"
								helpText="Character limit: 2000 (Minimum 30)"
							/>
						)}
					/>
				</FormItem>
				<FormItem>
					<Controller
						name="nickname"
						control={control}
						rules={{
							validate: (value) => resolveValidation('nickname', { ...getValues(), nickname: value }),
						}}
						render={({ field, fieldState }) => (
							<TextInput
								input={field}
								meta={{
									touched: fieldState.isTouched || fieldState.invalid,
									error: fieldState.error?.message,
									valid: !fieldState.invalid,
								}}
								label="Your name"
							/>
						)}
					/>
				</FormItem>
				<FormItem>
					<Controller
						name="email"
						control={control}
						rules={{
							validate: (value) => resolveValidation('email', { ...getValues(), email: value }),
						}}
						render={({ field, fieldState }) => (
							<TextInput
								input={field}
								meta={{
									touched: fieldState.isTouched || fieldState.invalid,
									error: fieldState.error?.message,
									valid: !fieldState.invalid,
								}}
								label="Your email address"
								type="email"
							/>
						)}
					/>
				</FormItem>
				{requireConfirmationCode && (
					<FormItem>
						<Controller
							name="confirmationCode"
							control={control}
							rules={{
								validate: (value) => resolveValidation('confirmationCode', { ...getValues(), confirmationCode: value }),
							}}
							render={({ field, fieldState }) => (
								<TextInput
									input={field}
									meta={{
										touched: fieldState.isTouched || fieldState.invalid,
										error: fieldState.error?.message,
										valid: !fieldState.invalid,
									}}
									label="Confirmation code"
									readOnly={confirmationCode ? 'readOnly' : null}
								/>
							)}
						/>
					</FormItem>
				)}
				<div className="tos-text">
					<Text size="small">
						By submitting this review, I acknowledge and agree to
						the{' '}
						<Link
							href="/legal/reviewstoc.html"
							target="_blank"
							rel="noopener noreferrer"
						>
							Terms and Conditions.
						</Link>
					</Text>
				</div>
				{!!formError && <IconText type="alert">{formError}</IconText>}
				<div className="form-submit">
					<Button
						type="submit"
						disabled={isSubmitting}
						className="submit-button"
					>
						Submit review
					</Button>
				</div>
			</Form>
		</div>
	);
}

ReviewForm.propTypes = {
	confirmationCode: PropTypes.string,
	onSubmit: PropTypes.func,
	facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	requireConfirmationCode: PropTypes.bool,
};

export default ReviewForm;
